import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export interface GenerationState {
  isRegeneratingLyrics: boolean;
  // Image regeneration state
  selectedImageUrl: string | null;
  selectedImageId: 1 | 2 | null;
  regeneratedImageUrl: string | null;
  isRegeneratingImage: boolean;
  // Music regeneration state
  selectedSongId: 1 | 2 | null;
  currentMusicTaskId: string | null;
  currentMusicPrompt: string | null;
  currentMusicStyle: string | null;
  currentMusicThumbnailUrl: string | null;
  regeneratedTaskId: string | null;
  isRegeneratingMusic: boolean;
  musicCoverOverrides: Partial<Record<1 | 2, string>>;
}

const initialState: GenerationState = {
  isRegeneratingLyrics: false,
  selectedImageUrl: null,
  selectedImageId: null,
  regeneratedImageUrl: null,
  isRegeneratingImage: false,
  selectedSongId: null,
  currentMusicTaskId: null,
  currentMusicPrompt: null,
  currentMusicStyle: null,
  currentMusicThumbnailUrl: null,
  regeneratedTaskId: null,
  isRegeneratingMusic: false,
  musicCoverOverrides: {},
};

export const generationSlice = createSlice({
  name: 'generation',
  initialState,
  reducers: {
    setSelectedImageUrl: (state, action: PayloadAction<string | null>) => {
      state.selectedImageUrl = action.payload;
    },
    setSelectedImageId: (state, action: PayloadAction<1 | 2 | null>) => {
      state.selectedImageId = action.payload;
    },
    clearSelection: (state) => {
      state.selectedImageUrl = null;
      state.selectedImageId = null;
    },
    setRegeneratedImageUrl: (state, action: PayloadAction<string | null>) => {
      state.regeneratedImageUrl = action.payload;
    },
    setSelectedSongId: (state, action: PayloadAction<1 | 2 | null>) => {
      state.selectedSongId = action.payload;
    },
    setCurrentMusicContext: (
      state,
      action: PayloadAction<{
        taskId: string;
        prompt: string;
        musicStyle?: string;
        thumbnailUrl?: string;
      }>,
    ) => {
      state.currentMusicTaskId = action.payload.taskId;
      state.currentMusicPrompt = action.payload.prompt;
      state.currentMusicStyle = action.payload.musicStyle || null;
      state.currentMusicThumbnailUrl = action.payload.thumbnailUrl || null;
    },
    setRegeneratedTaskId: (state, action: PayloadAction<string | null>) => {
      state.regeneratedTaskId = action.payload;
    },
    setIsRegeneratingImage: (state, action: PayloadAction<boolean>) => {
      state.isRegeneratingImage = action.payload;
    },
    setIsRegeneratingMusic: (state, action: PayloadAction<boolean>) => {
      state.isRegeneratingMusic = action.payload;
    },
    setIsRegeneratingLyrics: (state, action: PayloadAction<boolean>) => {
      state.isRegeneratingLyrics = action.payload;
    },
    setMusicCoverOverride: (
      state,
      action: PayloadAction<{ songId: 1 | 2; imageUrl: string | null }>,
    ) => {
      const { songId, imageUrl } = action.payload;
      if (imageUrl) {
        state.musicCoverOverrides[songId] = imageUrl;
      } else {
        delete state.musicCoverOverrides[songId];
      }
    },
    resetMusicCoverOverrides: (state) => {
      state.musicCoverOverrides = {};
    },
  },
});

export const {
  setSelectedImageUrl,
  setSelectedImageId,
  clearSelection,
  setRegeneratedImageUrl,
  setSelectedSongId,
  setCurrentMusicContext,
  setRegeneratedTaskId,
  setIsRegeneratingImage,
  setIsRegeneratingMusic,
  setIsRegeneratingLyrics,
  setMusicCoverOverride,
  resetMusicCoverOverrides,
} = generationSlice.actions;

export default generationSlice.reducer;
